import { ConstantValue } from "three.quarks";
import type { Emitter, EmitterSpec, VfxSystem } from "./index";

// Camera-wrapped weather (decision 0047: "camera-wrapped weather (wind-blown petals and leaves, light passing rain)";
// suggestions: camera-wrapped weather particles): one emitter held over the camera, its particles simulated in world
// space (so they don't slide with the view), its rate scaled by how much of the weather there is. Rain is the first
// user: a sheet of stretched streaks falling from above the camera. The emitter is built once, at boot (so its program
// compiles before ready), and only its rate changes after.

/** What the weather emitter is: its particles (any `EmitterSpec`, usually a `box` shape aimed down) and where it sits
 *  over the camera. */
export interface CameraWeatherSpec {
  /** The particles at full strength (`rate` is the rate at amount 1). */
  emitter: EmitterSpec;
  /** Metres above the camera the sheet sits. */
  above: number;
  /** Metres the sheet leads the camera along its view (so the rain is in front of it). Default 0. */
  ahead?: number;
}

/** Planted defects for the settle (a game's repeat check must turn red with each on). */
export const WEATHER_FAULTS = {
  /** `settle` keeps the drops already falling instead of clearing them first. */
  settleKeepsDrops: false,
  /** `settle` draws its drops from `Math.random` instead of the stream it is given. */
  settleUnseeded: false,
};

/** How `settle` runs the sheet forward: `seconds` of weather in `step`-second slices, every random draw from `random`
 *  (a seeded stream, e.g. @rpg/harness's `seededRandom`). */
export interface WeatherSettle {
  seconds: number;
  step: number;
  random: () => number;
}

/** A camera-wrapped weather emitter. */
export interface CameraWeather {
  readonly emitter: Emitter;
  /** Follow the camera (`position`, and `forward` for the lead) and set the amount, 0 to 1 (0 stops emitting; the
   *  falling particles finish). */
  update(position: { x: number; y: number; z: number }, amount: number, forward?: { x: number; z: number }): void;
  /** The amount last set. */
  amount(): number;
  /**
   * For a capture only, never in play: put the weather in a state that depends on nothing but `position`, `amount`,
   * `forward` and the stream. Every drop still falling is cleared; at an amount above 0 the sheet then runs
   * `o.seconds` forward in `o.step` slices with `Math.random` swapped for `o.random` (three.quarks draws from it), so
   * two captures of the same shot get the same drops. Returns the drops left.
   */
  settle(position: { x: number; y: number; z: number }, amount: number, o: WeatherSettle, forward?: { x: number; z: number }): number;
}

/** Add a camera-wrapped weather emitter to `vfx` (aimed straight down). */
export function cameraWeather(vfx: VfxSystem, spec: CameraWeatherSpec): CameraWeather {
  const emitter = vfx.add(spec.emitter, { direction: [0, -1, 0] });
  const rate = new ConstantValue(0);
  emitter.system.emissionOverTime = rate;
  let now = 0;
  const self: CameraWeather = {
    emitter,
    update(p, amount, forward) {
      now = Math.max(0, Math.min(1, amount));
      rate.value = spec.emitter.rate * now;
      const lead = spec.ahead ?? 0;
      const fl = forward ? Math.hypot(forward.x, forward.z) : 0;
      const ox = fl > 1e-6 ? (forward!.x / fl) * lead : 0;
      const oz = fl > 1e-6 ? (forward!.z / fl) * lead : 0;
      emitter.object.position.set(p.x + ox, p.y + spec.above, p.z + oz);
    },
    amount: () => now,
    settle(p, amount, o, forward) {
      if (!(o.seconds >= 0) || !(o.step > 0)) throw new Error(`weather settle: bad seconds ${o.seconds} or step ${o.step}`);
      self.update(p, amount, forward);
      const system = emitter.system;
      if (!WEATHER_FAULTS.settleKeepsDrops) system.restart();
      if (now <= 0) return system.particleNum;
      emitter.object.updateMatrixWorld(true);
      const real = Math.random;
      if (!WEATHER_FAULTS.settleUnseeded) Math.random = o.random;
      try {
        // whole slices: the same count every capture (a seconds that isn't a multiple of the step rounds)
        const n = Math.round(o.seconds / o.step);
        // this emitter alone (three.quarks types its step private; the batched renderer would step every emitter)
        const step = system as unknown as { update(dt: number): void };
        for (let i = 0; i < n; i++) step.update(o.step);
      } finally {
        Math.random = real;
      }
      return system.particleNum;
    },
  };
  return self;
}
